import React, { createContext, useCallback, useMemo, useContext } from 'react'
import { useSearchParam } from 'react-use'

import { useAudienceName } from '../../../Chat/blocs/useAudienceName'
import { useOnApproved, useOnDenied } from '../messaging'
import {
  JoinToSpeakIDSearchParamName,
  JoinToSpeakPresenterTokenSearchParamName,
} from '../constants'
import type {
  JoinToSpeakApprovedEvent,
  JoinToSpeakDeniedEvent,
} from '../messaging/types'
import { useCancelGuestRequestMutation } from '../use-cancel-guest-request-mutation'
import { useRequestGuestMutation } from '../use-request-guest-mutation'
import { clearJoinToSpeakSearchParams } from '../search-params'
import { useIsJoiningToSpeak } from '..'

import type {
  JoinToSpeakAudienceContextValue,
  JoinToSpeakAudienceProviderProps,
} from './types'

const JoinToSpeakAudienceContext =
  createContext<JoinToSpeakAudienceContextValue | null>(null)

const JoinToSpeakAudienceProvider = ({
  children,
  sessionId,
  token,
}: JoinToSpeakAudienceProviderProps) => {
  const requestGuestMutation = useRequestGuestMutation()
  const cancelGuestRequestMutation = useCancelGuestRequestMutation()
  const joinToSpeakID = useSearchParam(JoinToSpeakIDSearchParamName)
  const joinToSpeakPresenterToken = useSearchParam(
    JoinToSpeakPresenterTokenSearchParamName
  )
  const [, setIsJoiningToSpeak] = useIsJoiningToSpeak(sessionId)
  const name = useAudienceName()

  const requestToJoin = useCallback(
    async (question: string) => {
      const response = await requestGuestMutation.mutateAsync({
        sessionId,
        token,
        name,
        question: question.trim(),
      })

      return {
        joinToSpeakID: response.liveSpeakerId,
        joinToSpeakPresenterToken: response.presenterToken,
      }
    },
    [requestGuestMutation, sessionId, token, name]
  )

  const cancelRequestToJoin = useCallback(async () => {
    if (joinToSpeakPresenterToken === null || joinToSpeakID === null) {
      return
    }

    await cancelGuestRequestMutation.mutateAsync({
      presenterToken: joinToSpeakPresenterToken,
      sessionId,
      speakerId: joinToSpeakID,
    })
  }, [
    cancelGuestRequestMutation,
    joinToSpeakPresenterToken,
    sessionId,
    joinToSpeakID,
  ])

  const handleApprove = useCallback(
    async (event: JoinToSpeakApprovedEvent) => {
      if (event.data.joinToSpeakID === joinToSpeakID) {
        const url = new URL(event.data.roomLink)
        url.searchParams.set(JoinToSpeakIDSearchParamName, joinToSpeakID)
        url.searchParams.set('ff', '1')
        setIsJoiningToSpeak(true)

        window.location.href = url.toString()
      }
    },
    [joinToSpeakID, setIsJoiningToSpeak]
  )

  const handleDeny = useCallback(
    (event: JoinToSpeakDeniedEvent) => {
      if (event.data.joinToSpeakID === joinToSpeakID) {
        clearJoinToSpeakSearchParams()
      }
    },
    [joinToSpeakID]
  )

  useOnApproved(handleApprove)

  useOnDenied(handleDeny)

  const context = useMemo<JoinToSpeakAudienceContextValue>(
    () => ({
      requestToJoin,
      cancelRequestToJoin,
    }),
    [requestToJoin, cancelRequestToJoin]
  )

  return (
    <JoinToSpeakAudienceContext.Provider value={context}>
      {children}
    </JoinToSpeakAudienceContext.Provider>
  )
}

function useJoinToSpeakAsAudience() {
  const context = useContext(JoinToSpeakAudienceContext)

  if (context === null) {
    throw new Error(
      'useJoinToSpeakAsAudience must be used within a JoinToSpeakAudienceProvider'
    )
  }

  return context
}

export { JoinToSpeakAudienceProvider, useJoinToSpeakAsAudience }
